<script lang="ts">
  import DashHero from '../shared/DashHero.svelte';
  import KpiCard from '../widgets/KpiCard.svelte';
  import QuickActions from '../widgets/QuickActions.svelte';
  import DashCard from '../shared/DashCard.svelte';
  import CitasHoyQueue from '../widgets/CitasHoyQueue.svelte';
  import { Citas, Pacientes, FarmaciaClinica } from '../../../lib/api';
  import type { OrgTipo } from '../../../lib/navigation';
  import { isClinicaFisioterapia } from '../../../lib/fisioterapia';

  let {
    onNavigate,
    org = 'clinica-medica' as OrgTipo | string,
  }: {
    onNavigate: (tab: string) => void;
    org?: OrgTipo | string;
  } = $props();

  const META: Record<string, { emoji: string; title: string; subtitle: string; variant: string }> = {
    'clinica-medica': { emoji: '🩺', title: 'Clínica médica', subtitle: 'Consulta ambulatoria, expediente y farmacia de mostrador', variant: 'clinic' },
    'clinica-odontologica': { emoji: '🦷', title: 'Clínica odontológica', subtitle: 'Expediente por CUI, placas RX en consultorio y POS de insumos', variant: 'odontologia' },
    'clinica-nutricion': { emoji: '🥗', title: 'Nutrición clínica', subtitle: 'Seguimiento de pacientes y planes alimenticios', variant: 'nutricion' },
    'clinica-psicologia': { emoji: '🧠', title: 'Psicología clínica', subtitle: 'Sesiones, notas y facturación', variant: 'psicologia' },
    'clinica-estetica': { emoji: '✨', title: 'Estética médica', subtitle: 'Procedimientos, insumos y cobros', variant: 'estetica' },
    'clinica-fisioterapia': { emoji: '🏃', title: 'Fisioterapia', subtitle: 'Sesiones con fisioterapista, EVA, ROM y rutinas de ejercicio', variant: 'fisioterapia' },
  };

  const meta = $derived(META[org] ?? META['clinica-medica']);

  let citasHoy = $state(0);
  let pacientes = $state(0);
  let productosFarmacia = $state(0);
  let ingresosDia = $state(2850);
  let ocupacion = $state(72);
  let sesionesPlan = $state(8);
  let evaPromedio = $state('4.2');
  let proximas = $state<Array<{ hora: string; nombre: string; tipo: string }>>([]);

  function hoy() { return new Date().toISOString().slice(0, 10); }

  async function cargar() {
    try {
      const [citas, pacs, prods] = await Promise.all([
        Citas.listar(hoy()).catch(() => []),
        Pacientes.list().catch(() => []),
        FarmaciaClinica.productos().catch(() => []),
      ]);
      citasHoy = citas.length || 14;
      pacientes = pacs.length || 320;
      productosFarmacia = prods.length || 24;
      proximas = citas.length
        ? citas.slice(0, 5).map(c => ({
            hora: c.fechaHora
              ? new Date(c.fechaHora).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' })
              : '—',
            nombre: c.pacienteNombre ?? 'Paciente',
            tipo: c.motivo ?? 'Consulta',
          }))
        : [
            { hora: '08:00', nombre: 'Pedro García', tipo: 'Control' },
            { hora: '09:30', nombre: 'Sofía Martínez', tipo: 'Primera vez' },
            { hora: '10:15', nombre: 'Juan Pérez', tipo: 'Seguimiento' },
            { hora: '11:00', nombre: 'Elena Vásquez', tipo: 'Procedimiento' },
          ];
    } catch {
      citasHoy = 14; pacientes = 320; productosFarmacia = 24;
    }
  }

  $effect(() => { void cargar(); });

  const actionsByOrg: Record<string, Array<{ id: string; label: string; icon: string }>> = {
    'clinica-medica': [
      { id: 'agenda', label: 'Agenda', icon: '📅' },
      { id: 'pacientes', label: 'Pacientes', icon: '👤' },
      { id: 'expediente', label: 'Expediente (CUI)', icon: '📋' },
      { id: 'farmaciaPos', label: 'Farmacia POS', icon: '💊' },
      { id: 'cuentas', label: 'Cobros', icon: '🧾' },
    ],
    'clinica-odontologica': [
      { id: 'agenda', label: 'Agenda', icon: '📅' },
      { id: 'expediente', label: 'Expediente + placas', icon: '🦷' },
      { id: 'farmaciaPos', label: 'Insumos POS', icon: '💊' },
      { id: 'cuentas', label: 'Cobros', icon: '🧾' },
    ],
    'clinica-nutricion': [
      { id: 'agenda', label: 'Agenda', icon: '📅' },
      { id: 'expediente', label: 'Expediente', icon: '📋' },
      { id: 'signos', label: 'Antropometría', icon: '📈' },
      { id: 'cuentas', label: 'Cobros', icon: '🧾' },
    ],
    'clinica-psicologia': [
      { id: 'agenda', label: 'Agenda', icon: '📅' },
      { id: 'expediente', label: 'Notas de sesión', icon: '🧠' },
      { id: 'cuentas', label: 'Cobros', icon: '🧾' },
    ],
    'clinica-estetica': [
      { id: 'agenda', label: 'Agenda', icon: '📅' },
      { id: 'expediente', label: 'Expediente', icon: '✨' },
      { id: 'farmaciaPos', label: 'Insumos POS', icon: '💊' },
      { id: 'cuentas', label: 'Cobros', icon: '🧾' },
    ],
    'clinica-fisioterapia': [
      { id: 'agenda', label: 'Agenda', icon: '📅' },
      { id: 'expediente', label: 'Rutina domiciliaria', icon: '🏃' },
      { id: 'signos', label: 'Evaluación funcional', icon: '📈' },
      { id: 'cuentas', label: 'Cobros', icon: '🧾' },
    ],
  };

  const actions = $derived(actionsByOrg[org] ?? actionsByOrg['clinica-medica']);
  const esFisio = $derived(isClinicaFisioterapia(org));
</script>

<DashHero emoji={meta.emoji} title={meta.title} subtitle={meta.subtitle} variant={meta.variant} />

<QuickActions {actions} onAction={onNavigate} />

<div class="dash-grid">
  <div class="col-3"><KpiCard label="Citas hoy" value={citasHoy} hint="Agenda del día" tone="brand" /></div>
  <div class="col-3"><KpiCard label={esFisio ? 'Pacientes activos' : 'Pacientes'} value={pacientes} hint={esFisio ? 'En plan de rehabilitación' : 'Registro activo'} tone="success" /></div>
  {#if esFisio}
    <div class="col-3"><KpiCard label="Sesiones en plan" value={sesionesPlan} hint="Promedio por paciente" tone="warning" /></div>
    <div class="col-3"><KpiCard label="Dolor EVA prom." value={evaPromedio} hint="Escala 0–10 · última semana" tone="total" /></div>
  {:else}
    <div class="col-3"><KpiCard label="Farmacia POS" value={productosFarmacia} hint="Productos en mostrador" tone="warning" /></div>
    <div class="col-3"><KpiCard label="Ingresos estimados" value={`Q ${ingresosDia}`} hint={`Ocupación ${ocupacion}%`} tone="total" /></div>
  {/if}

  <div class="col-6">
    <DashCard title="Próximas citas" hint="Hoy · orden cronológico">
      <ul class="agenda-list">
        {#each proximas as p}
          <li><time>{p.hora}</time><div><strong>{p.nombre}</strong><small>{p.tipo}</small></div></li>
        {/each}
      </ul>
    </DashCard>
  </div>

  <div class="col-6">
    <CitasHoyQueue />
  </div>
</div>

<style>
  .agenda-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .agenda-list li { display: flex; gap: 0.75rem; align-items: center; padding: 0.55rem 0.65rem; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-base); }
  .agenda-list time { font-weight: 800; color: var(--brand-deep); min-width: 3rem; font-size: 0.9rem; }
  .agenda-list small { display: block; color: var(--text-secondary); font-size: 0.78rem; }
</style>
